import './EditPostModal.css';

import React, { useEffect, useState } from 'react';
import B2BPopUpForm from '../../b2b-common/B2BPopUpForm';
import { ConnectWithoutContact } from '@mui/icons-material';
import B2BStepper from '../../b2b-common/B2BStepper';
import PayloadGeneratorFactory from '../components/payload/PayloadGeneratorFactory';
import EditText from '../../b2b-common/EditText';
import BooleanToggleField from '../../b2b-common/BooleanToggleField';
import { B2BDatePicker } from '../../b2b-plans/components/dates/DatesRangeEditor';
import dayjs from 'dayjs';
import { generateSelectField } from '../../b2b-common/helpers';
import B2BMaskedPassword from '../../b2b-common/B2BMaskedPassword';
import { showNotification } from '../../b2b-common/Notification';
import { createTemplateInstance, updateInstance } from '../../../services/b2b-social-apiServices';
import { useLoginContext } from '../../../App';
import { useContentContext } from '../../layout/Layout';
import useB2BTranslation from '../../b2b-common/hooks/useB2BTranslation';
import { cloneDeep, isEqual } from 'lodash';

const blankStaticPost = {
    postInstanceType: 'STATIC', 
    socialPostInstanceId: 0,
    socialPostInstanceOrganizationUUID: '',
    socialPostTemplate: {
        socialPostTemplateId: 0,
    },
    socialPostInstanceLocale: 'es', // es, en, ca
    socialPostInstanceName: '',
    socialPostInstanceDescription: '',
    socialPostInstanceEnabled: true,
    socialPostInstancePrivacy: 'PUBLIC', // PUBLIC, PRIVATE
    socialPostInstancePassPhrase: '',
    socialPostInstanceExpirationDate: dayjs().add(1, 'month').endOf('day').format('YYYY-MM-DD HH:mm:ss'),
    socialPostInstanceVisits: 0,
    creationDate: null,
    lastModified: null,
    socialPostInstancePayload: null, // for STATIC posts
}

const EditPostModal = ({ operation = 'CREATE', post = null, template, open, onClose, onSavedPost }) => {
    
    const { t } = useB2BTranslation('b2b-social');
    const { loginData } = useLoginContext();
    const { setNotification } = useContentContext();

    const [currentPost, setCurrentPost] = useState(post ? post : blankStaticPost);
    const [currentStep, setCurrentStep] = useState(0);
    const [customPayloadReady, setCustomPayloadReady] = useState(false);

    useEffect(() => {
        // console.log('EditPostModal - post:', post);
        if (open) {
            if (!post && template && operation === 'CREATE') {
                const updatedPostWithTemplate = { ...blankStaticPost, socialPostTemplate: template };
                setCurrentPost(updatedPostWithTemplate);
            } 
            if (post && template && operation === 'UPDATE') {
                const clonedPost = cloneDeep(post);
                const updatedPostWithTemplate = { ...clonedPost, socialPostTemplate: template };
                setCurrentPost(updatedPostWithTemplate);
            }
        }
    }, [open, post]);
    
    const handlePostChange = (post) => {
        if (!isEqual(post, currentPost)) {
            setCurrentPost(post);
        }
    }

    const handleCustomPayloadReady = (payloadReady) => {
        setCustomPayloadReady(payloadReady);
    }

    const postPrivacyOptions = [
        { value: 'PUBLIC', label: t('Public') },
        { value: 'PRIVATE', label: t('Private') },
    ];

    const localeOptions = [
        { value: 'es', label: t('Spanish') },
        { value: 'en', label: t('English') },
        { value: 'ca', label: t('Catalan') },
        { value: 'fr', label: t('French') },
        { value: 'de', label: t('German') },
        { value: 'it', label: t('Italian') },
        { value: 'pt', label: t('Portuguese') },
    ];
    
    const filteredLocaleOptions = template?.templateLocales
        ? localeOptions.filter(option => template.templateLocales.includes(option.value))
        : localeOptions;

    const steps = [
        {
            label: t('Step 1 - Generate the payload'),
            description: t('Select the relevant content that you would like to share in your social post.'),
            content: (<PayloadGeneratorFactory post={currentPost} onPostChange={handlePostChange} onPayloadReady={handleCustomPayloadReady} />),
            disableNext: !customPayloadReady,
        },
        {
            label: t('Step 2 - Setup the post'),
            description: t('Setup the post, prodiding a name and description, jointly with an expiration date and some privacy settings. Note that the name and locale are mandatory fields.'),
            content: (
                <div className='social-post-common-setup'>
                    <div className='social-post-name-description'>
                        <EditText label={t('Name of the post')} object={currentPost} setObjectField={setCurrentPost} objectField={'socialPostInstanceName'} />
                        <BooleanToggleField label={t('Enabled')} field='socialPostInstanceEnabled' object={currentPost} setObject={setCurrentPost} />
                        <EditText label={t('Description (optional)')} object={currentPost} setObjectField={setCurrentPost} objectField={'socialPostInstanceDescription'} multiline={true} rows={3} />
                    </div>
                    <div className='social-post-expiration-privacy'>
                        <B2BDatePicker
                            objectHoldingDate={currentPost}
                            setObjectHoldingDate={setCurrentPost}
                            objectDateField={'socialPostInstanceExpirationDate'}
                            label={t('Expiration date')}
                            showTime={true}
                            value={currentPost.socialPostInstanceExpirationDate}
                            disabledDateRanges={[]}
                            className={'social-post-expiration-date'}
                        />
                        {generateSelectField(filteredLocaleOptions,
                            {
                                label: t('Locale'),
                                value: currentPost.socialPostInstanceLocale,
                                onChange: (e) => setCurrentPost({ ...currentPost, socialPostInstanceLocale: e.target.value }),
                                className: 'social-post-locale-select',
                            }
                        )}
                        {generateSelectField(postPrivacyOptions,
                            {
                                label: t('Post privacy'),
                                value: currentPost.socialPostInstancePrivacy,
                                onChange: (e) => setCurrentPost({ ...currentPost, socialPostInstancePrivacy: e.target.value }),
                                className: 'social-post-privacy-select',
                            }
                        )}
                        {currentPost.socialPostInstancePrivacy === 'PRIVATE' && (
                            <B2BMaskedPassword label={t('Password')} value={currentPost.socialPostInstancePassPhrase} 
                                onChange={(e) => setCurrentPost({ ...currentPost, socialPostInstancePassPhrase: e.target.value })}
                                className={'social-post-passphrase'}
                            />
                        )}

                    </div>
                </div>
            ),
            disableNext: false,
        },
    ]

    const handleSavePost = async () => { 
        if (!post) { // if post is null, then it is a new post
            try {
                // before saving I should stringify the payload
                const toSavePost = { ...currentPost };
                toSavePost.socialPostInstancePayload = JSON.stringify(toSavePost.socialPostInstancePayload);
                const newPost = await createTemplateInstance(loginData.accessToken, currentPost.socialPostTemplate.socialPostTemplateId, toSavePost);
                showNotification(setNotification, t('Social post created successfully'), 'success', 3000);
                onSavedPost(newPost);
                onClose();
            } catch (error) {
                showNotification(setNotification, t('Error creating the social post'), 'error', 5000);
            }
        } else { // if post is not null, then it is an update
            try {
                // before saving I should stringify the payload
                const toSavePost = { ...currentPost };
                toSavePost.socialPostInstancePayload = JSON.stringify(toSavePost.socialPostInstancePayload);
                toSavePost.lastModified = null; // to avoid conversion errors
                const updatedPost = await updateInstance(loginData.accessToken, currentPost.socialPostInstanceId, toSavePost);
                showNotification(setNotification, t('Social post updated successfully'), 'success', 3000);
                onSavedPost(updatedPost);
                onClose();
            } catch (error) {
                showNotification(setNotification, t('Error updating the social post'), 'error', 5000);
            }
        }
    }

    return (
        <B2BPopUpForm
            icon={<ConnectWithoutContact />}
            open={open}
            title={t('Social post')}
            onClose={onClose}
            maxWidth='lg'
            className={'new-social-post-modal'}
        >
            <B2BStepper
                steps={steps}
                className={'new-social-post-stepper'}
                onSuccess={handleSavePost }
                onCancel={ onClose }
                currentStep={currentStep}
            />
        </B2BPopUpForm>
    )
}

export default EditPostModal;